import { FC } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetFooter, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '@/lib/utils';
import { trpc } from 'src/trpc';
import { useToast } from 'src/components/useToast';
import { User } from 'lucide-react';
import {
  CustomerFormFields,
  customerFormZ,
  customerFormDefaults,
  toCreateCustomerInput,
  type CustomerFormData,
} from 'src/components/customers/CustomerFormFields';

interface CustomerSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CustomerSheet: FC<CustomerSheetProps> = ({ isOpen, onClose }) => {
  const { successToast, errorToast } = useToast();
  const utils = trpc.useUtils();

  const form = useForm<CustomerFormData>({
    resolver: zodResolver(customerFormZ),
    defaultValues: customerFormDefaults,
    mode: 'onTouched',
  });

  const createCustomerMutation = trpc.admin.customer.adminCreateCustomer.useMutation({
    onSuccess: () => {
      successToast({
        title: 'Customer created',
        description: 'New customer has been successfully created',
      });
      utils.admin.customer.adminListCustomers.invalidate();
      form.reset();
      onClose();
    },
    onError: (error) => {
      errorToast({
        title: 'Failed to create customer',
        description: error.message || 'An error occurred while creating the customer',
      });
    },
  });

  const onSubmit = async (data: CustomerFormData) => {
    createCustomerMutation.mutate(toCreateCustomerInput(data));
  };

  const handleSubmitClick = async () => {
    const isValid = await form.trigger();
    if (isValid) {
      const values = form.getValues();
      onSubmit(values);
    }
  };

  const handleClose = () => {
    if (!createCustomerMutation.isPending) {
      form.reset();
      onClose();
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <SheetContent
        side="right"
        className="p-0 sm:max-w-[600px] lg:max-w-[700px] xl:max-w-[800px] h-full"
      >
        <div className="grid h-full grid-rows-[auto_1fr_auto]">
          {/* Header */}
          <SheetHeader className="border-b bg-white px-6 py-4">
            <div className="flex items-center space-x-3">
              <div className="rounded-lg bg-orange-100 p-2">
                <User className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <SheetTitle className="text-xl font-semibold text-gray-900">
                  Create New Customer
                </SheetTitle>
              </div>
            </div>
          </SheetHeader>

          {/* Content Area - Scrollable */}
          <div className="overflow-y-auto px-6 py-6">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} id="customer-form" className="space-y-8">
                <CustomerFormFields form={form} />
              </form>
            </Form>
          </div>

          {/* Footer */}
          <SheetFooter className="border-t bg-gray-50 px-6 py-4">
            <div className="flex w-full items-center justify-end gap-3">
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={createCustomerMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSubmitClick}
                disabled={createCustomerMutation.isPending}
                className={cn(
                  'rounded-lg transition-all shadow-sm hover:shadow',
                  createCustomerMutation.isPending
                    ? 'bg-primary/50 cursor-not-allowed'
                    : 'bg-primary hover:bg-primary/90',
                )}
              >
                {createCustomerMutation.isPending ? 'Creating...' : 'Create Customer'}
              </Button>
            </div>
          </SheetFooter>
        </div>
      </SheetContent>
    </Sheet>
  );
};
